import { useQuery } from "@tanstack/react-query";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import { getConversationList, getQueryList } from "../../services";
import { convertToSelectOptions } from "../../utils";
import {
  convertCustomStatusToActual,
  createCustomStatusOptions,
} from "../../utils/status-filter-utils";
import { fetchStatusListQuery, fetchTopicListQuery } from "./useReferenceData";

export const parseUnreadConversationCount = (payload: any): number => {
  if (!payload) {
    return 0;
  }

  const raw =
    payload.UNREAD_CONVERSATIONS_COUNT ??
    payload.unread_conversations_count ??
    payload.metadata?.UNREAD_CONVERSATIONS_COUNT;

  const count = Number(raw);
  return Number.isNaN(count) ? 0 : count;
};

const fetchUnreadConversationCount = async (
  providerNpi: string
): Promise<number> => {
  let topicIds = "";

  try {
    const topicResponse = await fetchTopicListQuery();
    const topicOptions = convertToSelectOptions(topicResponse?.data);
    topicIds = topicOptions
      .map((option: { value: string }) => option.value)
      .join(",");
  } catch (error) {
    console.error("Failed to fetch topics for message count:", error);
  }

  const baseParams = {
    provider_npi: providerNpi,
    limit: 1,
    page: 0,
    topic_id: topicIds,
    search: "",
  };

  const response = await getConversationList({
    params: {
      ...baseParams,
      status: "OPEN,CLOSED",
    },
  });

  const unreadCount = parseUnreadConversationCount(response?.data);
  if (unreadCount > 0) {
    return unreadCount;
  }

  const openResponse = await getConversationList({
    params: {
      ...baseParams,
      status: "OPEN",
    },
  });

  const openCount = Number(openResponse?.data?.total_count ?? 0);
  return Number.isNaN(openCount) ? 0 : openCount;
};

const fetchMyQueriesOpenCount = async (providerNpi: string): Promise<number> => {
  const statusResponse = await fetchStatusListQuery();
  const originalOptions = convertToSelectOptions(statusResponse?.data);
  const { statusMapping } = createCustomStatusOptions(originalOptions);
  const statusIds = convertCustomStatusToActual(
    ["open_and_in_progress"],
    statusMapping
  );

  const response = await getQueryList({
    params: {
      provider_npi: providerNpi,
      status_id: statusIds.length > 0 ? statusIds.join(",") : "",
      topic_id: "",
      query_type: "",
      filter_id: "",
      start_date: "",
      end_date: "",
      limit: 1,
      page: 0,
      search: "",
    },
  });

  return response?.data?.total_count ?? 0;
};

export const useUnreadConversationCount = (
  providerNpi?: string,
  options?: { polling?: boolean; enabled?: boolean }
) => {
  const polling = options?.polling ?? false;
  const enabled = options?.enabled ?? Boolean(providerNpi);

  return useQuery({
    queryKey: queryKeys.unreadConversationCount(providerNpi ?? ""),
    queryFn: () => fetchUnreadConversationCount(providerNpi!),
    enabled: enabled && Boolean(providerNpi),
    refetchInterval: polling ? POLLING_INTERVAL_MS : false,
  });
};

export const useMyQueriesOpenCount = (
  providerNpi?: string,
  options?: { polling?: boolean; enabled?: boolean }
) => {
  const polling = options?.polling ?? false;
  const enabled = options?.enabled ?? Boolean(providerNpi);

  return useQuery({
    queryKey: queryKeys.myQueriesOpenCount(providerNpi ?? ""),
    queryFn: () => fetchMyQueriesOpenCount(providerNpi!),
    enabled: enabled && Boolean(providerNpi),
    refetchInterval: polling ? POLLING_INTERVAL_MS : false,
  });
};
